/**
 * Module dependencies.
 */

import React, { useCallback, useMemo } from 'react';
import { createDrugsTemplate } from '../drugs/form-config';
import { useTranslation } from 'react-i18next';
import { CrudAdd } from 'app/components/organisms/crud/crud-add';
import { useParams } from 'react-router-dom';
import { appRoutes } from 'app/routes';
import { Loading } from 'app/components/atoms/loading/loading';
import { Button } from 'app/components/atoms/button/button';
import styles from './convert-missing-drug.module.less';
import { usePostMissingDrugAI } from 'app/hooks/app-requests/missing-drugs/use-post-missing-drug-ai';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useGetMissingDrugOccurrences } from 'app/hooks/app-requests/missing-drugs/use-get-missing-drug-occurrences';

/**
 * Export `ConvertMissingDrug` component.
 */

export function ConvertMissingDrug(): JSX.Element {
  const [translate] = useTranslation();
  const { code } = useParams<{ code: string }>();
  const queryOpts = useMemo(
    () => ({
      pageSize: 1
    }),
    []
  );
  const { data: occurrences, isLoading } = useGetMissingDrugOccurrences(code, queryOpts);
  const firstOccurrence = occurrences?.results?.[0];
  const template = useMemo(() => {
    const t = createDrugsTemplate(translate);

    return { ...t, add: { ...t.add!, redirect: appRoutes.dashboard.medical.missingDrugs } };
  }, [translate]);

  const { mutateAsync: missingDrugsAI } = usePostMissingDrugAI();
  const handlePress = useCallback(
    async (form: UseFormReturn<FieldValues, any>, mainLanguage: string) => {
      const values = form.getValues();

      const result = await missingDrugsAI({
        code: code ?? '',
        language: mainLanguage,
        values: JSON.stringify(values)
      });
      const response = JSON.parse(result);

      form.reset(response);
    },
    [code, missingDrugsAI]
  );

  const payload = useMemo(() => {
    if (!firstOccurrence?.user?.language) {
      return { registry: code };
    }

    return {
      registry: code,
      name: JSON.stringify({
        [firstOccurrence.user.language]: firstOccurrence.name
      })
    };
  }, [code, firstOccurrence]);

  return (
    <Loading isLoading={isLoading}>
      <CrudAdd
        data={payload}
        template={template}
        topChildren={({ form, mainLanguage }) => (
          <div className={styles.extraActions}>
            <Button
              onClick={() => {
                handlePress(form, mainLanguage);
              }}
            >
              {translate('common.fillOpenAI')}
            </Button>
          </div>
        )}
      />
    </Loading>
  );
}
